import { useEffect } from 'react'
import { FormInstance } from 'antd'

interface UseProductsListProps {
  filteredProducts: any[]
  parentName: number
  form: FormInstance
}

export const useProductsList = ({ filteredProducts, parentName, form }: UseProductsListProps) => {
  useEffect(() => {
    const formValues = form.getFieldsValue()
    const currentProducts = formValues?.items?.[parentName]?.order_request?.related_products ?? []

    const existingProductsMap: Record<string, number[]> = {};
    currentProducts.forEach((prod, index) => {
      const uuid = prod.product?.uuid ?? prod.uuid
      if (uuid) {
        if (!existingProductsMap[uuid]) {
          existingProductsMap[uuid] = [];
        }
        existingProductsMap[uuid].push(index)
      }
    })

    const productsToAdd = []
    const processedProducts = new Set()

    filteredProducts.forEach(product => {
      const uuid = product.uuid ?? product.product?.uuid
      const max = product.pivot?.max ?? product.product?.pivot?.max ?? 1

      if (processedProducts.has(uuid)) return
      processedProducts.add(uuid)

      const existingCount = existingProductsMap[uuid]?.length ?? 0
      const toAdd = Math.max(0, max - existingCount)

      for (let i = 0; i < toAdd; i++) {
        productsToAdd.push({
          product: product.product ?? product,
          name: product.name ?? product.product?.name,
          pivot: product.pivot ?? product.product?.pivot,
          items: product.items ?? []
        })
      }
    })

    if (productsToAdd.length > 0) {
      const newProducts = [...currentProducts, ...productsToAdd]
      form.setFieldsValue({
        items: {
          [parentName]: {
            order_request: {
              related_products: newProducts
            }
          }
        }
      })
    }
  }, [filteredProducts, parentName, form])

  const getProductCountMap = (fields: any[]) => {
    const productCountMap: Record<string, number> = {}
    fields.forEach(field => {
      const fieldValue = form.getFieldValue(['items', parentName, 'order_request', 'related_products', field.name])
      const uuid = fieldValue?.product?.uuid ?? fieldValue?.uuid
      if (uuid) {
        productCountMap[uuid] = (productCountMap[uuid] ?? 0) + 1
      }
    })
    return productCountMap
  }

  const shouldRenderProduct = (
    fieldValue: any,
    productCountMap: Record<string, number>,
    filteredProducts: any[]
  ) => {
    if (!fieldValue) return false

    const productUuid = fieldValue.product?.uuid ?? fieldValue.uuid
    if (!productUuid) return false

    const originalProductData = filteredProducts.find(
      (product) => (product.product?.uuid === productUuid || product.uuid === productUuid)
    )

    if (!originalProductData) return false

    const maxValue = originalProductData.pivot?.max ?? originalProductData.product?.pivot?.max ?? 1
    const currentCount = productCountMap[productUuid] ?? 0

    return currentCount <= maxValue
  }

  return {
    getProductCountMap,
    shouldRenderProduct
  }
}
